import './Style.scss';
import { ListItem } from 'Interfaces/Forms/QuestionInterface';
import { DataPoint } from 'Interfaces/DataPoint';
import * as React from 'react';
import { Button } from 'react-bootstrap';
import { DragDropProvider } from '@dnd-kit/react';
import FormUtils from 'views/SingleInstance/utils/FormUtils';
import { getFormUtils } from 'views/SingleInstance/utils/FormUtilsHolder';
import { useAppDispatch, useAppSelector } from 'index';
import { uniqueFormsSelector } from 'reducers/formsReducer';
import { isNullOrUndefined } from 'utils/utils';
import { fetchPOI } from 'actions/pois';
import { getLocalization } from 'global/global';
import { LoadingComponent } from 'views/components/LoadingComponent';
import { getLocationHierarchyQuerySelector, QueryFiltersInterface } from 'reducers/filtersMenuReducer';

import { initializeKanban, organizeStatusData } from './util';
import KanbanFilter from './Filter';
import CardsViewer from './CardsViewer';
import StatusColumn from './StatusColumn';
import UpdateStatus from './UpdateStatus';

interface DataLimit {
  offset: number;
  limit: number;
}

export interface DroppedDataPoint {
  oldStatus: string; // previous status
  status: string; // new status
  id: string; // id of the dropped datapoint
}

export default function KanbanBoard() {

  const selectedForm = useAppSelector(state => state.kanbanMenu.selectedForm);
  const forms = useAppSelector(state => uniqueFormsSelector(state));
  const filtersMenu = useAppSelector(state => state.filtersMenu);
  const locationHierarchyQuery = useAppSelector((state) => getLocationHierarchyQuerySelector(state));

  const dispatch = useAppDispatch();

  const formUtils = React.useRef<FormUtils | null>(null);
  const abortController = React.useRef<AbortController | null>(null);

  const [data, setData] = React.useState<DataPoint[][]>([]);
  const [fields, setFields] = React.useState<string[]>([]);

  const [statuses, setStatuses] = React.useState<ListItem[]>([]);
  const [length, setLength] = React.useState(0);
  const [severityId, setSeverityId] = React.useState('');

  const [extraFields, setExtraFields] = React.useState<string[]>([]);

  const [filterQuestions, setFilterQuestions] = React.useState<string[]>([]);
  const [questionFilters, setQuestionFilters] = React.useState<{[key: string]: string[]}>({});
  const [filterValues, setFilterValues] = React.useState<string[]>([]);

  const limit = React.useRef<DataLimit>({ offset: -1, limit: 100});
  const [allLoaded, setAllLoaded] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const [multiViewer, setMultiViewer] = React.useState(false);
  const [multiViewDataPoints, setMultiViewDataPoints] = React.useState<DataPoint[]>([]);

  const [dropped, setDropped] = React.useState<DroppedDataPoint | null>(null);

  React.useEffect(() => {
    const form = forms.find(f => f.ref === selectedForm);
    if (form) {
      formUtils.current = getFormUtils(form);
      setData([]);
      setLength(0);
      if (form.count > 100) {
        limit.current.offset = 0;
        limit.current.limit = 100;
      } else {
        limit.current.offset = -1;
      }
      const init = initializeKanban(formUtils.current);
      if (init) {
        setLocalStatus(init);
        setQuestionFilters({});
      }
    }
  }, [selectedForm]);

  React.useEffect(() => {
    if (extraFields.length) {
      const form = forms.find(f => f.ref === selectedForm);
      if (form) {
        formUtils.current = getFormUtils(form);
        const init = initializeKanban(formUtils.current);
        setLocalStatus(init);
        if (form.count > 100) {
          limit.current.offset = 0;
          limit.current.limit = 100;
        } else {
          limit.current.offset = -1;
        }
        loadData();
      }
    }
  }, [forms]);

  const setLocalStatus = (init) => {
    if (init.statuses) {
      setStatuses(() => init.statuses);
    }
    setSeverityId(() => init.severityId);
    setFilterQuestions(() => init.filterQuestions);
    setFields(() => init.fields);
    setExtraFields(() => init.extraFields);
  };

  React.useEffect(() => {
    if (fields.length) {
      if (limit.current.offset > -1) {
        limit.current.limit = 100;
        limit.current.offset = 0;
      }
      setData([]);
      setAllLoaded(false);
      loadData();
    }
  }, [fields, questionFilters, locationHierarchyQuery, filtersMenu]);

  React.useEffect(() => {
    let max = 0;
    data.forEach(d => {
      max = Math.max(max, d.length);
    });
    setLength(max);
  }, [data]);

  const showMultiViewer = (dp: DataPoint) => {
    setMultiViewer(true);

    setMultiViewDataPoints((dps) => {
      const index = [...dps].findIndex(d => d.id === dp.id);
      if (index > -1) {
        return dps;
      }
      return [...dps].concat(dp);
    });
  };

  const loadData = () => {
    setLoading(true);
    const filters: QueryFiltersInterface[] = [];
    const users = filtersMenu.selectedUsers.map((u) => u.id);
    if (users.length > 0) {
      filters.push({
        condition : 'IN', field : 'users', value : users.join(','), operator : '0', fieldOperator : '1'
      });
    }
    if (filtersMenu.selectedDates.from && filtersMenu.selectedDates.from !== '') {
      filters.push({
        condition : 'GREATER_THAN_OR_EQUAL',
        field: filtersMenu.selectedDates.filterBy,
        value: filtersMenu.selectedDates.from,
        operator: '0',
        fieldOperator: '1'
      });
    }
    if (filtersMenu.selectedDates.to && filtersMenu.selectedDates.to !== '') {
      filters.push({
        condition : 'LESS_THAN_OR_EQUAL',
        field: filtersMenu.selectedDates.filterBy,
        value: filtersMenu.selectedDates.to,
        operator: '0',
        fieldOperator: '1'
      });
    }
    const keys = Object.keys(questionFilters);
    const query = {...locationHierarchyQuery};
    keys.forEach(key => {
      if (questionFilters[key].length) {
        query[key] = `L${questionFilters[key].join(',')}L`;
      }
    });
    if (abortController.current) {
      abortController.current.abort();
    }
    query['kanbanStatus'] = `L${statuses.map(s => s.id).join(',')}L`;
    abortController.current = new AbortController();
    const extraParams: string[] = [];
    if (limit.current.offset > -1) {
      extraParams.push(`limit=${limit.current.limit}`);
      extraParams.push(`off=${limit.current.offset}`);
    }
    const response = dispatch(
      fetchPOI(
        selectedForm, abortController.current.signal, undefined, filters,
        query, fields.concat(filterQuestions).join(','), undefined, extraParams
      )
    );
    response.then(res => res.json()).then(json => {
      if (Array.isArray(json)) {
        if (limit.current.offset > -1) {
          if (json.length) {
            const total = json[0].totalCount;
            if (limit.current.offset + 100 < total) {
              limit.current.offset = limit.current.offset + 100;
            } else {
              setAllLoaded(true);
            }
          }
        }
        const newData = organizeStatusData(json, statuses);
        setData((oldData) => {
          if (oldData.length > 0) {
            for (let i = 0; i < newData.length; i++) {
              newData[i] = oldData[i].concat(newData[i]);
            }
          }
          return newData;
        });
        const tempFilterValues: string[] = [];
        json.forEach(dp => {
          filterQuestions.forEach(fq => {
            if (dp[fq]) {
              tempFilterValues.push(`${dp[fq]}`);
            }
          });
        });
        setFilterValues((old) => Array.from(new Set(old.concat(tempFilterValues))));
      }
      setLoading(false);
    }).catch(e => {
      console.error(e);
      setLoading(false);
    });
  };

  const updateDataPoint = (move: boolean) => {
    if (move && dropped) {
      let oldIndex = -1;
      let newIndex = -1;
      statuses.forEach((status, index) => {
        if (status.id === dropped.oldStatus) {
          oldIndex = index;
        } else if (status.id === dropped.status) {
          newIndex = index;
        }
      });
      if (oldIndex !== -1 && newIndex !== -1) {
        const dp = data[oldIndex].find(d => d.id === dropped.id);
        if (dp) {
          setData(data.map((d, i) => {
            if (i === oldIndex) {
              return d.filter(d => d.id !== dropped.id);
            } else if (i === newIndex) {
              return d.concat([{...dp, kanbanStatus: dropped.status}]);
            } else {
              return d;
            }
          }));
        }
      }
    }
    setDropped(null);
  };

  const getDivRows = React.useCallback(() => {
    const severityListItem = formUtils.current?.getQuestion(severityId)?.listItems?.listItem;

    const columns = statuses.map((status, index) => (
      <StatusColumn
        key={`kboard-${status.value}-${index}`}
        index={index}
        status={status}
        dataPoints={data[index] || []}
        severityListItem={severityListItem || []}
        extraFields={extraFields}
        formUtils={formUtils.current}
        showMultiViewer={(dp) => showMultiViewer(dp)}
      />
    ));
    return (
      <div className="row">
        <DragDropProvider
          onDragEnd={(event) => {
            const { source, target } = event.operation;
            if (target && source) {
              console.log(`Dropped ${source.id} onto ${target.id}`);
              const id = `${target.id}`.substring(`${target.id}`.indexOf('-') + 1, `${target.id}`.lastIndexOf('-'));
              setDropped({ status: id, id: `${source.id}`, oldStatus: `${source.data.status}` });
            }
          }}
        >
          {columns}
        </DragDropProvider>
      </div>
    );
  }, [length, data, multiViewDataPoints, multiViewer, dropped]);

  return (
    <div className="mt-3">
      {dropped !== null && (
        <UpdateStatus
          droppedDataPoint={dropped}
          selectedForm={selectedForm}
          reset={updateDataPoint}
        />
      )}
      <KanbanFilter
        questions={filterQuestions}
        formUtils={formUtils.current}
        filterValues={filterValues}
        setFilter={(a) => setQuestionFilters(a)}
      />
      <div className="container-fluid kanban-body mb-3">
        <div className="row header-row">
          {statuses.map((listIItem: ListItem, index) => (
            <div key={`kanban-th-${listIItem.id}`} className="col kanban-header">
              <span className="header-title">{listIItem.value}</span>
              <div className="counts mt-1">
                {isNullOrUndefined(data[index]) ? 0 : data[index].length} {getLocalization('tasksModuleBtn')}
              </div>
            </div>
          ))}
        </div>
        {getDivRows()}
      </div>
      {limit.current.offset > -1 && !allLoaded && (
        <div>
          <Button size="sm" onClick={loadData}>{getLocalization('loadMore')}</Button>
        </div>
      )}
      {loading ? (<LoadingComponent />) : null}
      {multiViewer && (
        <CardsViewer
          dataPoints={multiViewDataPoints}
          formId={selectedForm}
          show={multiViewer}
          onHide={() => {
            setMultiViewer(false);
            setMultiViewDataPoints([]);
          }}
        />
      )}
    </div>
  );
}
